/**
 * Default content for Slider slides.
 *
 * Shared by the paths that create slides — the slider's starter template and a
 * slide that mounts empty — so a freshly inserted slider and the inserter
 * preview both show real content instead of three sets of empty fields.
 *
 * Keep DEMO_SLIDES in sync with the `example` block in Slider/block.json;
 * block.json is static metadata and cannot import from here.
 */

/*
 * Five slides, not three: at the default 3-per-view a three-slide slider has
 * nothing to scroll, so Embla reports a single snap and the arrows and dots
 * hide themselves — a freshly inserted slider would not look like a slider.
 * Five fills two pages and leaves the controls live.
 *
 * Five is also the ceiling: these are the only landscape photos in the shared
 * demo library that suit a card row. The rest are logos, square headshots, flat
 * illustrations, or carry third-party branding. Repeating an image across
 * slides reads as a bug in a carousel, so the set stops here rather than pad.
 */
const DEMO_SLIDES = [
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-1.webp',
    alt: 'Deck art laid out on a workbench',
    title: 'Deck art',
    description: 'Hand-screened graphics, one deck at a time.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-2.webp',
    alt: 'Rider mid-trick at the park',
    title: 'Park sessions',
    description: 'Shot on a Tuesday, before the crowds.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-3.webp',
    alt: 'Wheels and bearings on a shop counter',
    title: 'Parts and setups',
    description: 'Every build starts with the right wheels.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/feature-highlights-with-images-1-img-4.webp',
    alt: 'Shopper tapping a payment screen on a phone at a desk',
    title: 'Checkout in seconds',
    description: 'Pay on any device, no account needed.',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/feature-highlights-with-images-1-img-6.webp',
    alt: 'Courier handing a parcel to a customer at the door',
    title: 'Delivered to your door',
    description: 'Tracked every step, from our bench to yours.',
  },
];

const TITLE_PLACEHOLDER = 'Slide title';
const DESCRIPTION_PLACEHOLDER = 'Slide text';

/**
 * Inner-block layout for one slide, pre-filled with demo content.
 *
 * Demo images are external URLs (no attachment id) — the same insert-by-link
 * shape the author gets from the image placeholder, so they render as-is until
 * swapped for real media.
 *
 * @param {number} index Slide position; cycles through DEMO_SLIDES so every
 *                       starter slide gets different content.
 * @return {Array} Inner-block template.
 */
function getSlideTemplate(index = 0) {
  const demo = DEMO_SLIDES[index % DEMO_SLIDES.length];

  return [
    ['core/image', { url: demo.url, alt: demo.alt }],
    [
      'core/heading',
      { level: 3, content: demo.title, placeholder: TITLE_PLACEHOLDER },
    ],
    [
      'core/paragraph',
      { content: demo.description, placeholder: DESCRIPTION_PLACEHOLDER },
    ],
  ];
}

/**
 * Structure-only template — no demo content.
 *
 * Used as a slide's own inner-blocks template, which only applies to slides
 * that mount with zero inner blocks: one the author added by hand, or one
 * saved before slides were seeded. Both are the author's own content, so it
 * seeds empty fields rather than injecting demo copy into their slider.
 *
 * Module-level constant on purpose: the block editor re-syncs inner blocks
 * whenever the template's identity changes, so this must not be rebuilt per render.
 */
export const SLIDE_BLANK_TEMPLATE = [
  ['core/image', { sizeSlug: 'large' }],
  ['core/heading', { level: 3, placeholder: TITLE_PLACEHOLDER }],
  ['core/paragraph', { placeholder: DESCRIPTION_PLACEHOLDER }],
];

/**
 * The slider's starter template: three demo-filled slides.
 *
 * Module-level constant for the same identity-stability reason as above.
 */
export const SLIDER_STARTER_SLIDES = DEMO_SLIDES.map((_, index) => [
  'brandy/slide',
  {},
  getSlideTemplate(index),
]);
